import { z } from 'zod/v3';
import {
  AssertionSourceSchema,
  ControlEvidenceStatusEnumSchema,
  EvidenceReviewStatusSchema,
} from '../generated/enums';

export const ProvenanceNodeTypeSchema = z.enum([
  'policy',
  'claim',
  'test',
  'submission',
  'pack',
  'backlink',
  'control',
]);
export type ProvenanceNodeType = z.infer<typeof ProvenanceNodeTypeSchema>;

export const ProvenanceRelationshipSchema = z.enum([
  'contains',
  'verifies',
  'collected_for',
  'packaged_in',
  'references',
  'maps_to',
]);
export type ProvenanceRelationship = z.infer<typeof ProvenanceRelationshipSchema>;

export const ProvenanceNodeSchema = z.object({
  id: z.string(),
  type: ProvenanceNodeTypeSchema,
  label: z.string(),
  assertionSource: AssertionSourceSchema.nullable().optional(),
  timestamp: z.string(),
  status: z.string().nullable().optional(),
  metadata: z.record(z.unknown()).optional(),
});
export type ProvenanceNode = z.infer<typeof ProvenanceNodeSchema>;

export const ProvenanceEdgeSchema = z.object({
  from: z.string(),
  to: z.string(),
  relationship: ProvenanceRelationshipSchema,
  createdBy: AssertionSourceSchema.optional(),
  createdAt: z.string().optional(),
});
export type ProvenanceEdge = z.infer<typeof ProvenanceEdgeSchema>;

export const ProvenanceChainSchema = z.object({
  nodes: z.array(ProvenanceNodeSchema),
  edges: z.array(ProvenanceEdgeSchema),
  rootId: z.string(),
});
export type ProvenanceChain = z.infer<typeof ProvenanceChainSchema>;

export const LineageClaimSummarySchema = z.object({
  id: z.string(),
  programId: z.string(),
  policyName: z.string(),
  policyDocumentId: z.string().nullable(),
  controlRef: z.string().nullable(),
  claimText: z.string(),
  verification: z.string(),
  implementationStatus: z.string(),
  claimSource: z.string(),
  assertionSource: AssertionSourceSchema.nullable(),
  updatedAt: z.string(),
});
export type LineageClaimSummary = z.infer<typeof LineageClaimSummarySchema>;

export const LineagePolicySummarySchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  title: z.string().nullable(),
});
export type LineagePolicySummary = z.infer<typeof LineagePolicySummarySchema>;

export const LineageBacklinkSummarySchema = z.object({
  id: z.string(),
  requirementId: z.string(),
  requirementText: z.string(),
  evidenceFileId: z.string().nullable(),
  screenshotId: z.string().nullable(),
  matchedText: z.string().nullable(),
  confidence: z.number().nullable(),
  confirmedAt: z.string().nullable(),
  assertionSource: AssertionSourceSchema.nullable(),
});
export type LineageBacklinkSummary = z.infer<typeof LineageBacklinkSummarySchema>;

export const LineagePackSummarySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  submissionId: z.string().nullable(),
  status: z.string(),
  signatureStatus: z.string(),
  signedAt: z.string().nullable(),
  tsaTimestampedAt: z.string().nullable(),
  createdAt: z.string(),
  assertionSource: AssertionSourceSchema.nullable(),
});
export type LineagePackSummary = z.infer<typeof LineagePackSummarySchema>;

export const LineageSubmissionSummarySchema = z.object({
  id: z.string(),
  statusId: z.string(),
  submittedAt: z.string(),
  reviewStatus: EvidenceReviewStatusSchema,
  overallScore: z.number().nullable(),
  sourceExecutionId: z.string().nullable(),
  assertionSource: AssertionSourceSchema.nullable(),
  evidencePack: LineagePackSummarySchema.nullable().optional(),
  backlinks: z.array(LineageBacklinkSummarySchema).optional(),
});
export type LineageSubmissionSummary = z.infer<typeof LineageSubmissionSummarySchema>;

export const LineageTestSummarySchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  taskCode: z.string().nullable(),
  status: ControlEvidenceStatusEnumSchema,
  templateId: z.string().nullable(),
  templateType: z.string().nullable(),
  controlRefs: z.array(z.string()),
  assertionSource: AssertionSourceSchema.nullable(),
  updatedAt: z.string(),
});
export type LineageTestSummary = z.infer<typeof LineageTestSummarySchema>;

export const ClaimChainTestItemSchema = z.object({
  test: LineageTestSummarySchema,
  submissions: z.array(LineageSubmissionSummarySchema),
});
export type ClaimChainTestItem = z.infer<typeof ClaimChainTestItemSchema>;

export const ClaimChainDTOSchema = z.object({
  claim: LineageClaimSummarySchema,
  tests: z.array(ClaimChainTestItemSchema),
  graph: ProvenanceChainSchema,
});
export type ClaimChainDTO = z.infer<typeof ClaimChainDTOSchema>;

export const PolicyChainSummarySchema = z.object({
  claims: z.number(),
  tests: z.number(),
  submissions: z.number(),
  evidence: z.number(),
});
export type PolicyChainSummary = z.infer<typeof PolicyChainSummarySchema>;

export const PolicyChainDTOSchema = z.object({
  policy: LineagePolicySummarySchema.extend({
    programId: z.string(),
  }),
  summary: PolicyChainSummarySchema,
  graph: ProvenanceChainSchema,
});
export type PolicyChainDTO = z.infer<typeof PolicyChainDTOSchema>;

export const PackResolutionMethodSchema = z.enum([
  'submission_chain',
  'status_metadata_chain',
  'no_claim_chain',
]);
export type PackResolutionMethod = z.infer<typeof PackResolutionMethodSchema>;

export const PackProvenanceDTOSchema = z.object({
  pack: LineagePackSummarySchema,
  resolutionMethod: PackResolutionMethodSchema,
  submission: LineageSubmissionSummarySchema.nullable(),
  test: LineageTestSummarySchema.nullable(),
  claims: z.array(
    z.object({
      claim: LineageClaimSummarySchema,
      policy: LineagePolicySummarySchema.nullable(),
    }),
  ),
  backlinks: z.array(LineageBacklinkSummarySchema),
  graph: ProvenanceChainSchema,
});
export type PackProvenanceDTO = z.infer<typeof PackProvenanceDTOSchema>;

export const TestChainDTOSchema = z.object({
  test: LineageTestSummarySchema,
  upstreamClaims: z.array(
    z.object({
      claim: LineageClaimSummarySchema,
      policy: LineagePolicySummarySchema.nullable(),
    }),
  ),
  downstreamEvidence: z.array(LineageSubmissionSummarySchema),
  graph: ProvenanceChainSchema,
});
export type TestChainDTO = z.infer<typeof TestChainDTOSchema>;

export const ControlChainDTOSchema = z.object({
  control: z.object({
    id: z.string(),
    programId: z.string(),
    controlRef: z.string(),
    title: z.string().nullable(),
    description: z.string().nullable(),
    frameworkId: z.string(),
  }),
  tests: z.array(
    z.object({
      test: LineageTestSummarySchema,
      claims: z.array(LineageClaimSummarySchema),
      evidence: z.array(LineageSubmissionSummarySchema),
    }),
  ),
  graph: ProvenanceChainSchema,
});
export type ControlChainDTO = z.infer<typeof ControlChainDTOSchema>;
